// =============================
// src/pages/Stock.jsx  (Refactored Full Version)
// =============================
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { getStockSummary, getStockHistory } from "../../api/material/extras";

import BoardTable from "../../components/common/board/BoardTable";
import BoardToolbar from "../../components/common/board/BoardToolbar";
import BoardPagination from "../../components/common/board/BoardPagination";
import SearchBar from "../../components/common/board/SearchFilterBar";
import PageHeader from "../../components/common/ui/PageHeader";

// ----------------------------
// Utilities
// ----------------------------
const numberFmt = (v) => {
  const n = Number(v);
  if (v == null || v === "") return "-";
  return Number.isFinite(n) ? n.toLocaleString() : "-";
};

// =============================
// MAIN COMPONENT
// =============================
export default function Stock() {
  const navigate = useNavigate();

  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [searchField, setSearchField] = useState("code");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [searchTrigger, setSearchTrigger] = useState(0);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [projectId] = useState(localStorage.getItem("projectId"));

  // ---- Multi Select ----
  const [selectedRowsMulti, setSelectedRowsMulti] = useState([]);

  // ---- History Modal ----
  const [selectedItem, setSelectedItem] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyErr, setHistoryErr] = useState("");

  // ---------------------------------------
  // Apply Search
  // ---------------------------------------
  const applySearch = useCallback(() => {
    setPage(1);
    setSearchTrigger((v) => v + 1);
  }, []);

  // ---------------------------------------
  // Fetch Stock List
  // ---------------------------------------
  const fetchData = useCallback(async () => {
    setLoading(true);
    setErr("");

    try {
      const data = await getStockSummary({
        q,
        page,
        page_size: pageSize,
        search_field: searchField,
        start_date: startDate,
        end_date: endDate,
        project: projectId,
      });

      console.log("🔵 [FETCH STOCK] raw data:", data);

      const items =
        (Array.isArray(data?.results) && data.results) ||
        (Array.isArray(data?.items) && data.items) ||
        [];

      console.log("🟢 [FETCH STOCK] parsed items:", items);

      // 하나씩 detail 확인
      items.forEach((it, idx) => {
        console.log(`🧩 row[${idx}]`, {
          item_id: it.item_id,
          code: it.item_code,
          name: it.item_name,
          qty: it.qty_on_hand,
          doc_dns: it.doc_dns,
        });
      });

      setRows(items);
      setTotal(data?.total ?? items.length);
    } catch (e) {
      console.error("❌ [FETCH STOCK ERROR]", e);
      const status = e?.response?.status;
      setErr(`재고 목록 조회 실패${status ? ` (HTTP ${status})` : ""}.`);
    } finally {
      setLoading(false);
      setSelectedRowsMulti([]); // 검색 시 선택 초기화
    }
  }, [q, page, pageSize, searchField, startDate, endDate, projectId]);

  // Fetch when dependencies change
  useEffect(() => {
    fetchData();
  }, [fetchData, page, pageSize, searchTrigger]);

  // ---------------------------------------
  // Select Row Toggle
  // ---------------------------------------
  const toggleSelect = useCallback((row) => {
    setSelectedRowsMulti((prev) =>
      prev.some((r) => r.item_id === row.item_id)
        ? prev.filter((r) => r.item_id !== row.item_id)
        : [...prev, row]
    );
  }, []);

  // ---------------------------------------
  // Navigate to Multi-Create Out Document
  // ---------------------------------------
  const gotoCreateOutMulti = useCallback(() => {
    if (selectedRowsMulti.length === 0) return;
    const ids = selectedRowsMulti.map((r) => r.item_id).join(",");
    navigate(`/project/${projectId}/material/gi/new?item_ids=${ids}`);
  }, [selectedRowsMulti, navigate, projectId]);

  // ---------------------------------------
  // Load Stock History
  // ---------------------------------------
  const openHistory = useCallback(
    async (item) => {
      setSelectedItem(item);
      setHistoryRows([]);
      setHistoryErr("");
      setHistoryLoading(true);

      try {
        const data = await getStockHistory({
          project: projectId,
          item_id: item.item_id,
        });

        const logs =
          (Array.isArray(data?.items) && data.items) ||
          (Array.isArray(data) && data) ||
          [];

        setHistoryRows(logs);
      } catch (e) {
        const status = e?.response?.status;
        setHistoryErr(
          `입출고 이력 조회 실패${status ? ` (HTTP ${status})` : ""}.`
        );
      } finally {
        setHistoryLoading(false);
      }
    },
    [projectId]
  );

  const closeHistory = () => {
    setSelectedItem(null);
    setHistoryRows([]);
    setHistoryErr("");
  };

  // ---------------------------------------
  // Go to Out Document Creation (Single)
  // ---------------------------------------
  const gotoCreateOutSingle = () => {
    if (!selectedItem) return;

    navigate(
      `/project/${projectId}/material/gi/new?item_ids=${selectedItem.item_id}`
    );
  };

  // ---------------------------------------
  // Table Column Definition
  // ---------------------------------------
  const columns = useMemo(
    () => [
      {
        key: "multi",
        header: "",
        width: "40px",
        render: (row) => (
          <input
            type="checkbox"
            checked={selectedRowsMulti.some((r) => r.item_id === row.item_id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleSelect(row)}
          />
        ),
      },
      {
        key: "item_code",
        header: "품목 코드",
        render: (row) => row.item_code || "-",
      },
      {
        key: "item_name",
        header: "품명",
        render: (row) => row.item_name || "-",
      },
      {
        key: "unit_price",
        header: "기준단가",
        render: (row) => numberFmt(row.unit_price),
      },
      {
        key: "qty_on_hand",
        header: "현재고",
        render: (row) => numberFmt(row.qty_on_hand),
      },
    ],
    [selectedRowsMulti, toggleSelect]
  );

  // =============================
  // RENDER
  // =============================
  return (
    <div className="p-6">
      <PageHeader>재고 현황</PageHeader>

      <BoardToolbar />

      <div className="space-y-3">
        <BoardToolbar
          left={
            <button
              className="btn-create"
              disabled={selectedRowsMulti.length === 0}
              onClick={gotoCreateOutMulti}
            >
              선택 출고
            </button>
          }
          right={
            <SearchBar
              q={q}
              onChangeQ={setQ}
              onSearch={applySearch}
              searchField={searchField}
              onChangeSearchField={setSearchField}
              searchFieldOptions={[
                { value: "code", label: "품목 코드" },
                { value: "name", label: "품명" },
              ]}
              dateFilter={dateFilter}
              onChangeDateFilter={setDateFilter}
              startDate={startDate}
              endDate={endDate}
              onDateChange={({ start, end }) => {
                setStartDate(start);
                setEndDate(end);
              }}
              pageSize={pageSize}
              onChangePageSize={setPageSize}
            />
          }
        />

        <div className="text-muted mb-1">
          {loading ? "불러오는 중…" : `총 ${total.toLocaleString()}개`}
        </div>

        <BoardTable
          keyField="item_id"
          columns={columns}
          rows={rows}
          loading={loading}
          error={err}
          emptyText="데이터 없음"
          onRowClick={(row) => openHistory(row)}
        />

        <BoardPagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={(n) => setPage(n)}
        />
      </div>

      {selectedItem && (
        <HistoryModal
          item={selectedItem}
          rows={historyRows}
          loading={historyLoading}
          error={historyErr}
          onClose={closeHistory}
          onCreateOut={gotoCreateOutSingle}
        />
      )}
    </div>
  );
}

// =============================
// HISTORY MODAL
// =============================
function HistoryModal({ item, rows, loading, error, onClose, onCreateOut }) {
  const title = `${item.item_code} / ${item.item_name}`;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl p-6">
        {/* Header */}
        <div className="flex justify-between mb-4">
          <div>
            <div className="text-muted">입출고 이력</div>
            <div className="text-subtitle">{title}</div>
          </div>

          <div className="flex gap-2">
            <button className="btn-create-sm" onClick={onCreateOut}>
              출고서 작성
            </button>
            <button className="btn-basic-sm" onClick={onClose}>
              닫기
            </button>
          </div>
        </div>

        {/* Loading / Error */}
        {loading && <div className="text-muted mb-2">조회 중…</div>}
        {error && (
          <div className="text-red-600 text-sm border p-2 rounded mb-2 bg-red-50">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto rounded-xl border mt-2">
          <table className="min-w-full text-sm">
            <thead>
              <tr>
                <th className="doc-th">일자</th>
                <th className="doc-th">유형</th>
                <th className="doc-th">문서</th>
                <th className="doc-th">위치</th>
                <th className="doc-th text-right">변동수량</th>
                <th className="doc-th text-right">변동 후 재고</th>
                <th className="doc-th">비고</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const qty = Number(r.qty_change ?? r.qty);

                return (
                  <tr key={idx} className="border-t">
                    <td className="tbl-cell">{(r.date || "").slice(0, 10)}</td>
                    <td className="tbl-cell">{r.movement_type}</td>
                    <td className="tbl-cell">{r.doc_no}</td>
                    <td className="tbl-cell">{r.warehouse}</td>
                    <td className="tbl-cell text-right">
                      {qty < 0 ? "-" : "+"}
                      {Math.abs(qty).toLocaleString()}
                    </td>

                    <td className="tbl-cell text-right">
                      {numberFmt(r.qty_after)}
                    </td>

                    <td className="tbl-cell">{r.note || "-"}</td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-6 text-center text-gray-400"
                  >
                    이력이 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
